import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { getFeatureCreditUsage, updateLicense } from '../../../../utils/api_service/admin';
import { getSubscriptionByID } from '../../../../utils/api_service/subscription_api';
import type { CreditUsageResponse } from '../../../../utils/types/credits_types';
import { CANDI_BANK_CV } from '../../../../utils/types/credits_types';
import type { LicenseDateRange, OrganizationLicenses } from '../../../../utils/types/admin_types';
import { BILLING_LICENSE_CATALOG } from '../constants';
import type { BillingLicenseKey, IBillingLicenseCatalogItem } from '../constants/billingLicense.constants';
import { unixToDateString } from '../functions';
import type { ILicenseDateValue } from '../../components/LicenseCard';
import type { IPriceOverride } from '../interfaces';

export interface IUseBillingSubscription {
  organizationId: string;
  organizationLicenses: OrganizationLicenses;
  catalog: IBillingLicenseCatalogItem[];
  usage: CreditUsageResponse | null;
  overrides: IPriceOverride[];
  isSaveDisabled: boolean;
  isLicenseActive: (key: BillingLicenseKey) => boolean;
  toggleLicense: (key: BillingLicenseKey) => void;
  licenseStartDate: (key: BillingLicenseKey) => string | null;
  licenseEndDate: (key: BillingLicenseKey) => string | null;
  updateLicenseDate: (key: BillingLicenseKey, field: keyof LicenseDateRange, value: ILicenseDateValue) => void;
  monthlyCreditsValue: (key: BillingLicenseKey) => string;
  updateMonthlyCredits: (key: BillingLicenseKey, raw: string) => void;
  saveLicense: () => Promise<void>;
  handleRemoveOverride: (id: number) => void;
}

const EMPTY_LICENSES = (organizationId: string): OrganizationLicenses => ({
  organizationId,
  hierTalentBase: undefined,
  hierTalentAutoAssessments: undefined,
  hierTalentAutoClassify: undefined,
  hierTalentJobPost: undefined,
  hierTalentSmartInterviews: undefined,
  hierTalentCandiBank: undefined,
  hierTalentCca: undefined,
  hierTalentPsychometricFaxtor: undefined,
  hierTalentWellness: undefined,
  hierTalentReporting: undefined,
});

const INITIAL_OVERRIDES: IPriceOverride[] = [
  { id: 1, product: 'HierTalent Base', interval: 'Annual', companySize: '100-500 employees', amount: '12,500.00' },
  { id: 2, product: 'CandiBank', interval: 'Annual', companySize: '100-500 employees', amount: '5,000.00' },
];

export const useBillingSubscription = (): IUseBillingSubscription => {
  const { id } = useParams<{ id: string }>();
  const [usage, setUsage] = useState<CreditUsageResponse | null>(null);
  const [organizationLicenses, setOrganizationLicenses] = useState<OrganizationLicenses>(EMPTY_LICENSES(id));
  const [overrides, setOverrides] = useState<IPriceOverride[]>(INITIAL_OVERRIDES);

  useEffect(() => {
    void fetchSubscription();
    void fetchUsage();
  }, [id]);

  const fetchUsage = async (): Promise<void> => {
    try {
      const res = await getFeatureCreditUsage(id, CANDI_BANK_CV, 6);
      if (res.ok) setUsage(await res.json());
    } catch (e) {
      console.log(e);
    }
  };

  const fetchSubscription = async (): Promise<void> => {
    try {
      const res = await getSubscriptionByID(id);

      if (res.ok) {
        const result = await res.json();

        setOrganizationLicenses({
          organizationId: id,
          hierTalentBase: result.hierTalentBase
            ? { startDate: unixToDateString(result.hierTalentBase.startDate), endDate: unixToDateString(result.hierTalentBase.endDate) }
            : undefined,
          hierTalentAutoAssessments: result.hierTalentAutoAssessments
            ? { startDate: unixToDateString(result.hierTalentAutoAssessments.startDate), endDate: unixToDateString(result.hierTalentAutoAssessments.endDate) }
            : undefined,
          hierTalentAutoClassify: result.hierTalentAutoClassify
            ? { startDate: unixToDateString(result.hierTalentAutoClassify.startDate), endDate: unixToDateString(result.hierTalentAutoClassify.endDate) }
            : undefined,
          hierTalentJobPost: result.hierTalentJobPost
            ? { startDate: unixToDateString(result.hierTalentJobPost.startDate), endDate: unixToDateString(result.hierTalentJobPost.endDate) }
            : undefined,
          hierTalentSmartInterviews: result.hierTalentSmartInterviews
            ? { startDate: unixToDateString(result.hierTalentSmartInterviews.startDate), endDate: unixToDateString(result.hierTalentSmartInterviews.endDate) }
            : undefined,
          hierTalentCandiBank: result.hierTalentCandiBank
            ? {
              startDate: unixToDateString(result.hierTalentCandiBank.startDate),
              endDate: unixToDateString(result.hierTalentCandiBank.endDate),
              monthlyCredits: result.hierTalentCandiBank.monthlyCredits ?? null,
            }
            : undefined,
          hierTalentCca: result.hierTalentCca
            ? { startDate: unixToDateString(result.hierTalentCca.startDate), endDate: unixToDateString(result.hierTalentCca.endDate) }
            : undefined,
          hierTalentPsychometricFaxtor: result.hierTalentPsychometricFaxtor
            ? { startDate: unixToDateString(result.hierTalentPsychometricFaxtor.startDate), endDate: unixToDateString(result.hierTalentPsychometricFaxtor.endDate) }
            : undefined,
          hierTalentReporting: result.hierTalentReporting
            ? { startDate: unixToDateString(result.hierTalentReporting.startDate), endDate: unixToDateString(result.hierTalentReporting.endDate) }
            : undefined,
          hierTalentWellness: result.hierTalentWellness
            ? { startDate: unixToDateString(result.hierTalentWellness.startDate), endDate: unixToDateString(result.hierTalentWellness.endDate) }
            : undefined,
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const isLicenseActive = (key: BillingLicenseKey): boolean =>
    organizationLicenses[key] !== undefined;

  const toggleLicense = (key: BillingLicenseKey): void => {
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: prev[key] ? undefined : { startDate: '', endDate: '' },
    }));
  };

  const licenseKeys = BILLING_LICENSE_CATALOG.map((item) => item.licenseKey);
  const isSaveDisabled = licenseKeys.some((key) => {
    const license = organizationLicenses[key];
    return license !== undefined && (!license.startDate || !license.endDate);
  });

  const updateLicenseDate = (
    key: BillingLicenseKey,
    field: keyof LicenseDateRange,
    value: ILicenseDateValue,
  ): void => {
    if (field === 'monthlyCredits') return;
    const dateString = field === 'startDate' ? value.startDate ?? '' : value.endDate ?? '';
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: dateString },
    }));
  };

  const monthlyCreditsValue = (key: BillingLicenseKey): string => {
    const value = organizationLicenses[key]?.monthlyCredits;
    return value === null || value === undefined ? '' : String(value);
  };

  const updateMonthlyCredits = (key: BillingLicenseKey, raw: string): void => {
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : Math.max(0, Math.floor(Number(trimmed)));
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { startDate: '', endDate: '' }), monthlyCredits: Number.isNaN(parsed) ? null : parsed },
    }));
  };

  const saveLicense = async (): Promise<void> => {
    try {
      const res = await updateLicense(organizationLicenses);
      if (res.ok) {
        toast.success('License updated successfully.', { position: 'bottom-center' });
        void fetchUsage();
      }
    } catch (e) {
      toast.error('Only one file allowed', { position: 'bottom-center' });
    }
  };

  const handleRemoveOverride = (id: number): void => {
    setOverrides((prev) => prev.filter((override) => override.id !== id));
  };

  return {
    organizationId: id,
    organizationLicenses,
    catalog: BILLING_LICENSE_CATALOG,
    usage,
    overrides,
    isSaveDisabled,
    isLicenseActive,
    toggleLicense,
    licenseStartDate: (key) => organizationLicenses[key]?.startDate ?? null,
    licenseEndDate: (key) => organizationLicenses[key]?.endDate ?? null,
    updateLicenseDate,
    monthlyCreditsValue,
    updateMonthlyCredits,
    saveLicense,
    handleRemoveOverride,
  };
};
